import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Clock, IndianRupee, Car, ArrowRight, Save } from 'lucide-react';
import { onewayTripAPI, carAPI } from '../../services/api';
import { formatVehicleName } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

export default function CreateOneWayTrip() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEdit = !!id;
  const isDriver = user?.role === 'driver';

  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingCars, setFetchingCars] = useState(true);

  const [form, setForm] = useState({
    carId: '',
    fromCity: '',
    toCity: '',
    departureDate: '',
    departureTime: '',
    price: ''
  });

  useEffect(() => {
    fetchCars();
    if (isEdit) fetchTrip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchCars = async () => {
    try {
      const response = await carAPI.getAllCars();
      const allCars = response.data?.data || response.data?.cars || [];
      const activeCars = allCars.filter(c => c.status !== 'deleted' && c.status !== 'inactive');
      setCars(activeCars);

      // For driver role: auto-select if only one vehicle available
      if (isDriver && activeCars.length > 0 && !isEdit) {
        setForm(prev => ({ ...prev, carId: activeCars[0]._id }));
      }
    } catch (error) {
      toast.error('Failed to load vehicles');
    } finally {
      setFetchingCars(false);
    }
  };

  const fetchTrip = async () => {
    try {
      const response = await onewayTripAPI.getById(id);
      const trip = response.data?.data;
      if (trip) {
        setForm({
          carId: trip.carId?._id || trip.carId || '',
          fromCity: trip.fromCity || '',
          toCity: trip.toCity || '',
          departureDate: trip.departureDate ? new Date(trip.departureDate).toISOString().split('T')[0] : '',
          departureTime: trip.departureTime || '',
          price: trip.price || ''
        });
      }
    } catch (error) {
      toast.error('Failed to load trip details');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.carId || !form.fromCity || !form.toCity || !form.departureDate || !form.price) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      const selectedCar = cars.find(c => c._id === form.carId);
      const payload = {
        ...form,
        price: Number(form.price),
        seatsAvailable: selectedCar?.seats || 4
      };

      if (isEdit) {
        await onewayTripAPI.update(id, payload);
        toast.success('Trip updated successfully');
      } else {
        await onewayTripAPI.create(payload);
        toast.success('One-way trip listed successfully!');
      }
      navigate('/oneway-trips');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save trip');
    } finally {
      setLoading(false);
    }
  };

  const selectedCar = cars.find(c => c._id === form.carId);

  return (
    <div className="p-4 sm:p-6 max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/oneway-trips')} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold text-gray-900">
          {isEdit ? 'Edit Trip' : 'Add One-Way Trip'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-5 space-y-5">
        {/* Vehicle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
            <Car className="w-4 h-4 text-blue-600" /> Vehicle *
          </label>
          {fetchingCars ? (
            <p className="text-sm text-gray-500">Loading...</p>
          ) : isDriver ? (
            selectedCar ? (
              <div className="w-full border border-gray-200 bg-gray-50 rounded-lg px-4 py-2.5">
                <p className="text-sm font-medium text-gray-900">{formatVehicleName(selectedCar, { showReg: true })}</p>
                <p className="text-xs text-gray-500 mt-0.5">{selectedCar.vehicleType} · {selectedCar.fuelType} · {selectedCar.seats} seats</p>
              </div>
            ) : (
              <div className="w-full border border-red-200 bg-red-50 rounded-lg px-4 py-2.5">
                <p className="text-sm text-red-600">No vehicle assigned. Contact your admin.</p>
              </div>
            )
          ) : (
            <select
              name="carId"
              value={form.carId}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select vehicle</option>
              {cars.map(car => (
                <option key={car._id} value={car._id}>
                  {formatVehicleName(car, { showReg: true })}
                </option>
              ))}
            </select>
          )}
          {selectedCar && !isDriver && (
            <p className="text-xs text-gray-400 mt-1">{selectedCar.vehicleType} · {selectedCar.fuelType} · {selectedCar.seats} seats</p>
          )}
        </div>

        {/* From → To */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-green-600 mb-1.5 flex items-center gap-1">
              <MapPin className="w-4 h-4" /> From *
            </label>
            <input
              name="fromCity"
              value={form.fromCity}
              onChange={handleChange}
              placeholder="City"
              required
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 capitalize"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-red-600 mb-1.5 flex items-center gap-1">
              <ArrowRight className="w-4 h-4" /> To *
            </label>
            <input
              name="toCity"
              value={form.toCity}
              onChange={handleChange}
              placeholder="City"
              required
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 capitalize"
            />
          </div>
        </div>

        {/* Date & Time */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
              <Calendar className="w-4 h-4" /> Date *
            </label>
            <input
              type="date"
              name="departureDate"
              value={form.departureDate}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
              required
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
              <Clock className="w-4 h-4" /> Time
            </label>
            <input
              type="time"
              name="departureTime"
              value={form.departureTime}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Price */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
            <IndianRupee className="w-4 h-4" /> Price per person (₹) *
          </label>
          <input
            type="number"
            name="price"
            value={form.price}
            onChange={handleChange}
            placeholder="Price per seat"
            min="0"
            required
            className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Buttons */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate('/oneway-trips')}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEdit ? 'Update' : 'List Trip'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
